import { createCustomFilterForEntity } from "@shared/backend/create-custom-filter-for-entity";
import {
  filterWithRelationsAndJson,
  type RelationalOrJsonEntityFilter,
} from "@shared/helpers/sql-relations";
import type { Organization } from "../organization";
import type { Skill } from "../skill";
import type { Team } from "../team";
import { User } from "./";

const createCustomFilter = createCustomFilterForEntity<User>();

export class UserFilters {
  /**
   * # 🌟✨ HyperFilter – The Ultimate Filtering Powerhouse!
   * ### 🤝 Filter through complex relations effortlessly
   * ### 📦 Directly filter JSON fields (no backend work required!)
   * ### 🚀 Transforms your filter into a single, optimized DB query
   * ### 🔒 Engineered for maximum SQL injection protection
   *
   * ---
   * ### Example:
   * ```ts
   * myEntityRepo.find({
   *  where: MyEntity.hyperFilter({ ... })
   * })
   * ```
   *
   * ---
   * Built by Natan Salmon
   */
  static hyperFilter = createCustomFilter<RelationalOrJsonEntityFilter<User>>(
    (filters) => {
      return filterWithRelationsAndJson(User)(filters);
    },
  );

  static isInOrganization = createCustomFilter<{ organizationId?: string }>(
    ({ organizationId }) => {
      return this.hyperFilter({
        associations: {
          $some: {
            organizationId,
          },
        },
      });
    },
  );

  // Static isInGroup = createCustomFilter<{ groupId?: string }>(({ groupId }) => {
  //   return this.hyperFilter({
  //     groups: {
  //       $some: {
  //         groupId,
  //       },
  //     },
  //   });
  // });

  static isInOpportunity = createCustomFilter<{ opportunityId?: string }>(
    ({ opportunityId }) => {
      return this.hyperFilter({
        associations: {
          $some: {
            organization: {
              $is: {
                opportunities: {
                  $some: {
                    id: opportunityId,
                  },
                },
              },
            },
          },
        },
      });
    },
  );

  static isInTeam = createCustomFilter<{ team: string | Team }>(
    async ({ team }) => {
      return this.hyperFilter({
        associations: {
          $some: {
            teams: {
              $some: {
                teamId: typeof team === "string" ? team : team.id,
              },
            },
          },
        },
      });
    },
  );

  static isInOnOfTeams = createCustomFilter<{ teams?: (string | Team)[] }>(
    ({ teams }) => {
      return this.hyperFilter({
        associations: {
          $some: {
            teams: {
              $some: {
                teamId: {
                  $in: (teams ?? []).map((t) =>
                    typeof t === "string" ? t : t.id,
                  ),
                },
              },
            },
          },
        },
      });
    },
  );

  static doesHaveSkill = createCustomFilter<{ skill: Skill | string }>(
    ({ skill }) => {
      return this.hyperFilter({
        skills: {
          $some: {
            skillId: typeof skill === "string" ? skill : skill.id,
          },
        },
      });
    },
  );

  static hasRequestedOpportunityForOrganization = createCustomFilter<{
    organizationId: string;
  }>(({ organizationId }) => {
    return this.hyperFilter({
      associations: {
        $some: {
          requestedOpportunities: {
            $some: {
              organization: {
                $is: {
                  id: organizationId,
                },
              },
            },
          },
        },
      },
    });
  });

  static isInGroupOfOrganizationAsUserOrAdmin = createCustomFilter<{
    organization: string | Organization;
  }>(({ organization }) =>
    this.hyperFilter({
      $or: [
        {
          managedGroups: {
            $some: {
              organization: {
                $is: {
                  id:
                    typeof organization === "string"
                      ? organization
                      : organization.id,
                },
              },
            },
          },
        },
        // {
        //   groups: {
        //     $some: {
        //       group: {
        //         $is: {
        //           organization: {
        //             $is: {
        //               id:
        //                 typeof organization === "string"
        //                   ? organization
        //                   : organization.id,
        //             },
        //           },
        //         },
        //       },
        //     },
        //   },
        // },
      ],
    }),
  );
}
